import { useState } from "react";
import { Box } from "@mui-wrapper";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { RichTextField } from "@components/common/rich-text-field";
import { Button, Checkbox, Dialog, Switch, Text, useToast } from "@flat-ui";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { PlanEvents } from "@utils/track-event/track-event-list";

/** All scopes a feature can be assigned to */
const FEATURE_SCOPES: FaroCoreApiTypes.FeatureScope[] = [
  "company",
  "user",
  "group",
  "project",
  "device",
];

/** Feature identifiers are lower case words separated by dashes, e.g. "ai-indexing" */
const FEATURE_IDENTIFIER_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/;

interface Props {
  /** Optional callback invoked after a feature was created successfully */
  onFeatureCreated?: () => void;
}

/** Button and dialog to create a new single feature */
export function CreateFeatureDialog({
  onFeatureCreated,
}: Props): React.JSX.Element {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [identifier, setIdentifier] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [isDefaultActive, setIsDefaultActive] = useState<boolean>(true);
  const [scopes, setScopes] = useState<FaroCoreApiTypes.FeatureScope[]>([]);
  const [tags, setTags] = useState<string>("");

  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorHandler();
  const { openToast } = useToast();

  const isIdentifierValid = FEATURE_IDENTIFIER_REGEX.test(identifier);
  const isNameValid = name.trim().length > 0;
  const isScopesValid = scopes.length > 0;

  function handleDialogClose(): void {
    setIsDialogOpen(false);

    // Reset form to initial state
    setIdentifier("");
    setName("");
    setDescription("");
    setIsDefaultActive(true);
    setScopes([]);
    setTags("");
  }

  function toggleScope(scope: FaroCoreApiTypes.FeatureScope): void {
    setScopes((prevScopes) =>
      prevScopes.includes(scope)
        ? prevScopes.filter((prevScope) => prevScope !== scope)
        : [...prevScopes, scope]
    );
  }

  async function onCreateFeature(): Promise<void> {
    trackEvent({
      name: PlanEvents.createFeature,
      props: { identifier },
    });

    setIsCreating(true);
    try {
      await faroCoreApiClient.admin.createFeature({
        identifier,
        name: name.trim(),
        description: description.trim(),
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Property name is defined by the backend API
        defaultActive: isDefaultActive,
        scopes,
        tags: tags
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag.length > 0),
      });
      openToast({
        title: `Feature "${name.trim()}" created successfully`,
      });
      handleDialogClose();
      onFeatureCreated?.();
    } catch (error) {
      handleErrorWithToast({
        id: `create-feature-${Date.now().toString()}`,
        title: "Could not create feature",
        error,
      });
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <>
      <Button onClick={() => setIsDialogOpen(true)}>Create feature</Button>

      <Dialog
        open={isDialogOpen}
        title="Create feature"
        onCancel={handleDialogClose}
        onConfirm={onCreateFeature}
        showSpinner={isCreating}
        isConfirmDisabled={
          isCreating || !isIdentifierValid || !isNameValid || !isScopesValid
        }
      >
        <RichTextField
          label="Identifier"
          text={identifier}
          onChange={setIdentifier}
          helpTooltip="A human-readable primary key that is consistent between all deployments. It can't be changed later"
          validation={
            identifier.length === 0 || isIdentifierValid
              ? { isValid: true }
              : {
                  isValid: false,
                  message:
                    "Use lower case letters and numbers separated by dashes, e.g. ai-indexing",
                }
          }
        />

        <RichTextField
          label="Name"
          text={name}
          onChange={setName}
          helpTooltip="Human-readable name of the feature"
        />

        <RichTextField
          label="Description"
          text={description}
          onChange={setDescription}
        />

        <RichTextField
          label="Tags"
          text={tags}
          onChange={setTags}
          helpText="Comma separated list of tags"
        />

        <Box sx={{ mt: 2 }}>
          <Text variant="labelM">Scopes</Text>
          <Box sx={{ display: "flex", flexWrap: "wrap", ml: 1.375 }}>
            {FEATURE_SCOPES.map((scope) => (
              <Checkbox
                key={scope}
                label={scope}
                checked={scopes.includes(scope)}
                onChange={() => toggleScope(scope)}
              />
            ))}
          </Box>
        </Box>

        <Switch
          label="Active by default"
          checked={isDefaultActive}
          onToggle={() => setIsDefaultActive(!isDefaultActive)}
          sx={{ mt: 2, ml: 0 }}
        />
      </Dialog>
    </>
  );
}
